"use client";
import {
  Building2Icon,
  CalendarDaysIcon,
  DollarSignIcon,
  ExternalLinkIcon,
  LayoutDashboardIcon,
  LogInIcon,
  LogOutIcon,
  ShieldCheckIcon,
} from "lucide-react";
import { useSession } from "next-auth/react";
import Link from "next/link";
import { buildBookingUrl } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

export function AccountDropdown() {
  const { data: session, status } = useSession();

  if (status === "loading") {
    return (
      <Button size="sm" variant="outline" disabled className="text-sm">
        <div className="flex items-center gap-2">
          <div className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          Carregando...
        </div>
      </Button>
    );
  }

  if (!session?.user) {
    return (
      <Button size="sm" variant="outline" asChild className="text-sm">
        <Link href="/login" className="flex items-center gap-2">
          <LogInIcon className="size-4" /> Entrar
        </Link>
      </Button>
    );
  }

  const isPlatformAdmin = session.user.role === "PLATFORM_ADMIN";
  const tenantPage = buildBookingUrl(session.user.tenantSlug || "");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <Avatar>
          <AvatarImage
            src={isPlatformAdmin ? "/logo.png" : session?.user.tenantLogoUrl}
            alt={isPlatformAdmin ? "Platform Admin" : session?.user.tenantName}
            width={36}
            height={36}
            className="rounded-md"
          />
          <AvatarFallback>
            {isPlatformAdmin
              ? "PA"
              : session?.user?.tenantName?.substring(0, 2).toUpperCase()}
          </AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <div className="bg-accent/50 rounded-md text-center text-xs">
          <DropdownMenuLabel className="text-primary text-sm font-semibold">
            {isPlatformAdmin ? "Platform Admin" : session.user.tenantName}
          </DropdownMenuLabel>
          <DropdownMenuLabel className="text-muted-foreground">
            {session.user.email}
          </DropdownMenuLabel>
        </div>
        <DropdownMenuSeparator />

        {/* Links for Platform Admin */}
        {isPlatformAdmin ? (
          <>
            <DropdownMenuItem asChild>
              <Link href="/super">
                <ShieldCheckIcon />
                Super Admin
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link href="/super/tenants">
                <Building2Icon />
                Tenants
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link href="/super/comissoes">
                <DollarSignIcon />
                Comissões
              </Link>
            </DropdownMenuItem>
          </>
        ) : (
          /* Links for Tenant Users */
          <>
            <DropdownMenuItem asChild>
              <Link href="/dashboard">
                <LayoutDashboardIcon />
                Dashboard
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link href="/dashboard/agenda">
                <CalendarDaysIcon />
                Minha Agenda
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link href={tenantPage} target="_blank" rel="noopener noreferrer">
                <ExternalLinkIcon />
                Minha Página
              </Link>
            </DropdownMenuItem>
          </>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link href="/logout">
            <LogOutIcon />
            Sair
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
